/**
 * SyncDashboardPage — DRR-113 Chain Sync Session Dashboard
 *
 * Displays offline sync sessions, Blake3 chain verification, and conflict
 * resolution queue. MED_DOSAGE conflicts are life-critical and are sorted
 * first — they NEVER auto-resolve.
 *
 * All types imported from generated/graphql.ts.
 *
 * Route: /sync
 * Guards: Backend RequireScope("sync:read") on all queries
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Drawer,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconShield,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconCopy,
  IconRefresh,
  IconFilter,
} from '@tabler/icons-react';
import { useAuth } from '@/auth/AuthContext';
import type {
  SyncSession,
  SyncConflict,
  ChainVerificationResult,
  ResolveSyncConflictInput,
} from '@/generated/graphql';
import {
  GqlSyncStatus,
  GqlConflictReason,
  GqlConflictStatus,
  useSyncHistoryQuery,
  useSyncConflictsQuery,
  useSessionConflictsQuery,
  useChainVerificationQuery,
  useResolveSyncConflictMutation,
} from '@/generated/graphql';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleString();
}

function sessionStatusBadge(status: string) {
  switch (status) {
    case GqlSyncStatus.SUCCESS:
      return <Badge color="green" leftSection={<IconCheck size={12} />}>Success</Badge>;
    case GqlSyncStatus.PARTIAL:
      return <Badge color="yellow">Partial</Badge>;
    case GqlSyncStatus.CONFLICT:
      return <Badge color="red" leftSection={<IconAlertTriangle size={12} />}>Conflict</Badge>;
    case GqlSyncStatus.QUARANTINED:
      return <Badge color="violet">Quarantined</Badge>;
    case GqlSyncStatus.IN_PROGRESS:
      return <Badge color="blue">In Progress</Badge>;
    case GqlSyncStatus.REJECTED:
      return <Badge color="gray">Rejected</Badge>;
    default:
      return <Badge color="gray">{status}</Badge>;
  }
}

function conflictReasonBadge(reason: string) {
  switch (reason) {
    case GqlConflictReason.MED_DOSAGE:
      return <Badge color="red" size="sm">MED DOSAGE (LIFE-CRITICAL)</Badge>;
    case GqlConflictReason.CONCURRENT_EDIT:
      return <Badge color="yellow" size="sm">Concurrent Edit</Badge>;
    case GqlConflictReason.DUPLICATE_EVENT:
      return <Badge color="orange" size="sm">Duplicate Event</Badge>;
    case GqlConflictReason.ENTITY_NOT_FOUND:
      return <Badge color="gray" size="sm">Entity Not Found</Badge>;
    case GqlConflictReason.SCHEMA_VERSION_MISMATCH:
      return <Badge color="blue" size="sm">Schema Mismatch</Badge>;
    default:
      return <Badge color="gray" size="sm">{reason}</Badge>;
  }
}

function conflictStatusBadge(status: string) {
  switch (status) {
    case GqlConflictStatus.PENDING:
      return <Badge color="yellow" size="sm">Pending</Badge>;
    case GqlConflictStatus.IN_REVIEW:
      return <Badge color="blue" size="sm">In Review</Badge>;
    case GqlConflictStatus.ESCALATED:
      return <Badge color="orange" size="sm">Escalated</Badge>;
    case GqlConflictStatus.RESOLVED:
      return <Badge color="green" size="sm">Resolved</Badge>;
    default:
      return <Badge color="gray" size="sm">{status}</Badge>;
  }
}

/** Sort conflicts: MED_DOSAGE first (life-critical), then by status (PENDING first) */
function sortConflicts(conflicts: SyncConflict[]): SyncConflict[] {
  return [...conflicts].sort((a, b) => {
    // MED_DOSAGE always comes first
    if (a.reason === GqlConflictReason.MED_DOSAGE && b.reason !== GqlConflictReason.MED_DOSAGE) return -1;
    if (b.reason === GqlConflictReason.MED_DOSAGE && a.reason !== GqlConflictReason.MED_DOSAGE) return 1;
    // PENDING before others
    if (a.status === GqlConflictStatus.PENDING && b.status !== GqlConflictStatus.PENDING) return -1;
    if (b.status === GqlConflictStatus.PENDING && a.status !== GqlConflictStatus.PENDING) return 1;
    return 0;
  });
}

// ---------------------------------------------------------------------------
// ConflictCard (inside SyncConflictDrawer)
// ---------------------------------------------------------------------------

interface ConflictCardProps {
  readonly conflict: SyncConflict;
  readonly onResolved: () => void;
}

function ConflictCard({ conflict, onResolved }: Readonly<ConflictCardProps>) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [resolveSyncConflict] = useResolveSyncConflictMutation({
    refetchQueries: ['SessionConflicts', 'SyncConflicts', 'SyncHistory'],
  });

  const isMedDosage = conflict.reason === GqlConflictReason.MED_DOSAGE;
  const isResolved = conflict.status === GqlConflictStatus.RESOLVED;

  const handleResolve = useCallback(async (acceptedValue: Record<string, unknown>) => {
    if (isMedDosage && reason.trim().length < 10) {
      setError('MED_DOSAGE conflicts require a reason of at least 10 characters.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const input: ResolveSyncConflictInput = {
        conflictId: conflict.conflictId,
        acceptedValue,
        reason: reason.trim() || `Accepted value for ${conflict.reason} conflict`,
      };
      await resolveSyncConflict({ variables: { input } });
      onResolved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Resolution failed.');
    } finally {
      setLoading(false);
    }
  }, [conflict, reason, isMedDosage, resolveSyncConflict, onResolved]);

  return (
    <Card withBorder padding="md" radius="sm" style={{ borderColor: isMedDosage ? 'var(--mantine-color-red-4)' : undefined }}>
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            {conflictReasonBadge(conflict.reason)}
            {conflictStatusBadge(conflict.status)}
          </Group>
          <Stack gap={2} align="flex-end">
            <Text size="xs" c="dimmed">{conflict.entityType}</Text>
            <Text size="xs" ff="monospace">{conflict.entityId}</Text>
          </Stack>
        </Group>

        {conflict.approvalId && (
          <Text size="xs" c="dimmed">Approval ID: {conflict.approvalId}</Text>
        )}

        {!isResolved && (
          <SimpleGrid cols={2} spacing="sm">
            <Stack gap="xs">
              <Text size="xs" fw={600} c="blue">Device A Value</Text>
              <Text
                size="xs"
                ff="monospace"
                style={{
                  background: 'var(--mantine-color-blue-0)',
                  padding: '8px',
                  borderRadius: 4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                {JSON.stringify(conflict.deviceAValue, null, 2)}
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="xs" fw={600} c="teal">Hub / Device B Value</Text>
              <Text
                size="xs"
                ff="monospace"
                style={{
                  background: 'var(--mantine-color-teal-0)',
                  padding: '8px',
                  borderRadius: 4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                {conflict.deviceBValue !== null && conflict.deviceBValue !== undefined
                  ? JSON.stringify(conflict.deviceBValue, null, 2)
                  : '(not available — conflict with hub state)'}
              </Text>
            </Stack>
          </SimpleGrid>
        )}

        {isResolved ? (
          <Badge color="green" leftSection={<IconCheck size={12} />}>Resolved</Badge>
        ) : (
          <>
            <Textarea
              label={isMedDosage ? 'Resolution Reason (required for MED_DOSAGE)' : 'Resolution Reason (optional)'}
              placeholder={isMedDosage
                ? 'Explain the clinical basis for this resolution — minimum 10 characters...'
                : 'Optional reason for audit trail...'}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              minRows={2}
              required={isMedDosage}
            />

            {error && (
              <Alert color="red" icon={<IconAlertTriangle size={14} />}>
                {error}
              </Alert>
            )}

            <Group gap="sm">
              <Button
                size="sm"
                variant="light"
                color="blue"
                onClick={() => handleResolve(conflict.deviceAValue)}
                loading={loading}
                disabled={isMedDosage && reason.trim().length < 10}
                leftSection={<IconCheck size={14} />}
              >
                Accept Device A
              </Button>
              {conflict.deviceBValue !== undefined && conflict.deviceBValue !== null && (() => {
                const deviceB: Record<string, unknown> = conflict.deviceBValue;
                return (
                  <Button
                    size="sm"
                    variant="light"
                    color="teal"
                    onClick={() => handleResolve(deviceB)}
                    loading={loading}
                    disabled={isMedDosage && reason.trim().length < 10}
                    leftSection={<IconCheck size={14} />}
                  >
                    Accept Device B
                  </Button>
                );
              })()}
            </Group>
          </>
        )}
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// SyncConflictDrawer
// ---------------------------------------------------------------------------

interface SyncConflictDrawerProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly sessionId: string | null;
}

function SyncConflictDrawer({ opened, onClose, sessionId }: Readonly<SyncConflictDrawerProps>) {
  const { data, loading, error, refetch } = useSessionConflictsQuery({
    variables: { sessionId: sessionId ?? '' },
    skip: !sessionId || !opened,
    fetchPolicy: 'network-only',
  });

  const conflicts: SyncConflict[] = useMemo(
    () => sortConflicts(data?.sessionConflicts ?? []),
    [data],
  );

  const hasMedDosage = conflicts.some((c) => c.reason === GqlConflictReason.MED_DOSAGE);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Sync Conflicts"
      position="right"
      size="xl"
    >
      <Stack gap="md">
        {hasMedDosage && (
          <Alert color="red" variant="filled" icon={<IconAlertTriangle size={16} />}>
            MED_DOSAGE conflicts require Tier 3 auth and a mandatory reason.
            These are life-critical — never auto-resolve.
          </Alert>
        )}

        {loading && <Center p="xl"><Loader /></Center>}
        {error && <Alert color="red">{error.message}</Alert>}

        {!loading && !error && conflicts.length === 0 && (
          <Paper p="xl" withBorder>
            <Center>
              <Text size="sm" c="dimmed">No conflicts for this session.</Text>
            </Center>
          </Paper>
        )}

        {conflicts.map((conflict) => (
          <ConflictCard
            key={conflict.conflictId}
            conflict={conflict}
            onResolved={() => refetch()}
          />
        ))}
      </Stack>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// ChainVerificationDrawer
// ---------------------------------------------------------------------------

interface ChainVerificationDrawerProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly sessionId: string | null;
  readonly archiveKey: string | null | undefined;
}

function ChainVerificationDrawer({ opened, onClose, sessionId, archiveKey }: Readonly<ChainVerificationDrawerProps>) {
  const [copied, setCopied] = useState(false);

  const { data, loading, error } = useChainVerificationQuery({
    variables: { sessionId: sessionId ?? '' },
    skip: !sessionId || !opened,
    fetchPolicy: 'network-only',
  });

  const result: ChainVerificationResult | null = data?.chainVerification ?? null;

  const handleCopyKey = useCallback(() => {
    if (!archiveKey) return;
    navigator.clipboard.writeText(archiveKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Clipboard access may be denied in some environments
    });
  }, [archiveKey]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Chain Verification"
      position="right"
      size="lg"
    >
      <Stack gap="md">
        {loading && <Center p="xl"><Loader /></Center>}
        {error && <Alert color="red">{error.message}</Alert>}

        {result && (
          <>
            {/* Chain validity status */}
            {result.valid ? (
              <Alert color="green" variant="light" icon={<IconShield size={18} />}>
                Valid chain — all {result.entriesVerified} entries verified successfully.
              </Alert>
            ) : (
              <Alert color="red" variant="filled" icon={<IconAlertTriangle size={18} />}>
                Invalid chain — verification failed.
                {result.firstBrokenLink !== null && result.firstBrokenLink !== undefined && (
                  <> Chain broken at entry #{result.firstBrokenLink}.</>
                )}
              </Alert>
            )}

            {/* Verification details */}
            <Card withBorder padding="md" radius="sm">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm" fw={600}>Chain Status</Text>
                  {result.valid
                    ? <Group gap="xs"><IconShield size={16} color="var(--mantine-color-green-6)" /><Text size="sm" c="green">Valid</Text></Group>
                    : <Group gap="xs"><IconAlertTriangle size={16} color="var(--mantine-color-red-6)" /><Text size="sm" c="red">Invalid</Text></Group>
                  }
                </Group>

                <SimpleGrid cols={2} spacing="sm">
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">Entries Verified</Text>
                    <Text size="sm" fw={500}>{result.entriesVerified}</Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">Chain Length</Text>
                    <Text size="sm" fw={500}>{result.chainLength}</Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">Genesis Device</Text>
                    <Text size="sm" ff="monospace">{result.genesisDeviceId}</Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">Genesis Actor</Text>
                    <Text size="sm" ff="monospace">{result.genesisActorId}</Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed">Genesis Timestamp</Text>
                    <Text size="sm">{formatDate(result.genesisTimestamp)}</Text>
                  </Stack>
                  {result.firstBrokenLink !== null && result.firstBrokenLink !== undefined && (
                    <Stack gap={2}>
                      <Text size="xs" c="dimmed">First Broken Link</Text>
                      <Text size="sm" c="red" fw={600}>Entry #{result.firstBrokenLink}</Text>
                    </Stack>
                  )}
                </SimpleGrid>
              </Stack>
            </Card>

            {/* Archive key (Blake3 hash) */}
            {archiveKey && (
              <Card withBorder padding="md" radius="sm">
                <Stack gap="xs">
                  <Text size="sm" fw={600}>Wasabi Archive Key (Blake3)</Text>
                  <Group gap="xs" align="flex-start">
                    <Text
                      size="xs"
                      ff="monospace"
                      style={{
                        background: 'var(--mantine-color-gray-1)',
                        padding: '8px 12px',
                        borderRadius: 4,
                        wordBreak: 'break-all',
                        flex: 1,
                      }}
                    >
                      {archiveKey}
                    </Text>
                    <Tooltip label={copied ? 'Copied!' : 'Copy archive key'}>
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={handleCopyKey}
                        leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        color={copied ? 'green' : 'gray'}
                      >
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                    </Tooltip>
                  </Group>
                  <Text size="xs" c="dimmed">
                    WORM archive. Present this key to regulators to verify offline activity provenance.
                  </Text>
                </Stack>
              </Card>
            )}
          </>
        )}

        {!loading && !error && !result && (
          <Paper p="xl" withBorder>
            <Center>
              <Text size="sm" c="dimmed">No chain verification data available for this session.</Text>
            </Center>
          </Paper>
        )}
      </Stack>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function SyncDashboardPage() {
  const { tenant } = useAuth();

  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [deviceFilter, setDeviceFilter] = useState('');

  const [conflictDrawerOpened, conflictDrawerHandlers] = useDisclosure(false);
  const [chainDrawerOpened, chainDrawerHandlers] = useDisclosure(false);

  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedArchiveKey, setSelectedArchiveKey] = useState<string | null | undefined>(null);

  const { data, loading, error, refetch } = useSyncHistoryQuery({
    variables: {
      limit: 100,
      deviceId: deviceFilter.trim() || undefined,
    },
    fetchPolicy: 'network-only',
  });

  const sessions: SyncSession[] = useMemo(() => {
    const all: SyncSession[] = data?.syncHistory ?? [];
    if (!statusFilter) return all;
    return all.filter((s) => s.status === statusFilter);
  }, [data, statusFilter]);

  // Stats derived from all sessions (not filtered)
  const allSessions: SyncSession[] = useMemo(
    () => data?.syncHistory ?? [],
    [data],
  );
  const stats = useMemo(() => ({
    total: allSessions.length,
    success: allSessions.filter((s) => s.status === GqlSyncStatus.SUCCESS).length,
    conflict: allSessions.filter((s) => s.status === GqlSyncStatus.CONFLICT || s.status === GqlSyncStatus.PARTIAL).length,
    quarantined: allSessions.filter((s) => s.status === GqlSyncStatus.QUARANTINED || s.status === GqlSyncStatus.REJECTED).length,
  }), [allSessions]);

  const handleOpenConflicts = useCallback((session: SyncSession) => {
    setSelectedSessionId(session.sessionId);
    conflictDrawerHandlers.open();
  }, [conflictDrawerHandlers]);

  const handleOpenChainVerification = useCallback((session: SyncSession) => {
    setSelectedSessionId(session.sessionId);
    setSelectedArchiveKey(session.archiveKey);
    chainDrawerHandlers.open();
  }, [chainDrawerHandlers]);

  if (!tenant) {
    return <Alert color="yellow">No tenant context. Please select a tenant.</Alert>;
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading sync history" icon={<IconAlertTriangle size={16} />}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Group gap="sm">
          <IconShield size={28} />
          <Title order={3}>Chain Sync Dashboard</Title>
        </Group>
        <Tooltip label="Refresh sync history">
          <Button
            variant="subtle"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
        </Tooltip>
      </Group>

      {/* Stats cards */}
      {loading ? (
        <Center p="xl"><Loader /></Center>
      ) : (
        <>
          <SimpleGrid cols={4}>
            <Card withBorder padding="md" radius="md">
              <Stack gap="xs" align="center">
                <Text size="xl" fw={700}>{stats.total}</Text>
                <Text size="sm" c="dimmed">Total Sessions</Text>
              </Stack>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Stack gap="xs" align="center">
                <Text size="xl" fw={700} c="green">{stats.success}</Text>
                <Text size="sm" c="dimmed">Successful</Text>
              </Stack>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Stack gap="xs" align="center">
                <Text size="xl" fw={700} c={stats.conflict > 0 ? 'red' : 'dimmed'}>{stats.conflict}</Text>
                <Text size="sm" c="dimmed">With Conflicts</Text>
              </Stack>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Stack gap="xs" align="center">
                <Text size="xl" fw={700} c={stats.quarantined > 0 ? 'violet' : 'dimmed'}>{stats.quarantined}</Text>
                <Text size="sm" c="dimmed">Quarantined</Text>
              </Stack>
            </Card>
          </SimpleGrid>

          {/* Filter bar */}
          <Group gap="sm">
            <IconFilter size={16} />
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              clearable
              data={[
                { value: GqlSyncStatus.SUCCESS, label: 'Success' },
                { value: GqlSyncStatus.PARTIAL, label: 'Partial' },
                { value: GqlSyncStatus.CONFLICT, label: 'Conflict' },
                { value: GqlSyncStatus.QUARANTINED, label: 'Quarantined' },
                { value: GqlSyncStatus.IN_PROGRESS, label: 'In Progress' },
                { value: GqlSyncStatus.REJECTED, label: 'Rejected' },
              ]}
              style={{ width: 200 }}
            />
            <TextInput
              placeholder="Filter by device ID"
              value={deviceFilter}
              onChange={(e) => setDeviceFilter(e.currentTarget.value)}
              style={{ width: 240 }}
            />
          </Group>

          {/* Session table */}
          {sessions.length === 0 ? (
            <Paper p="xl" withBorder>
              <Center>
                <Stack align="center" gap="sm">
                  <IconShield size={48} color="gray" />
                  <Text size="lg" fw={500}>No Sync Sessions</Text>
                  <Text size="sm" c="dimmed">
                    No offline sync sessions match the current filters.
                  </Text>
                </Stack>
              </Center>
            </Paper>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Device ID</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Entries Grafted</Table.Th>
                  <Table.Th>Conflicts</Table.Th>
                  <Table.Th>Chain</Table.Th>
                  <Table.Th>Started</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sessions.map((session) => (
                  <Table.Tr key={session.sessionId}>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm" ff="monospace">{session.deviceId}</Text>
                        <Text size="xs" c="dimmed" ff="monospace">{session.actorId}</Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>{sessionStatusBadge(session.status)}</Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {session.entriesGrafted}/{session.entriesReceived}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c={session.entriesConflicted > 0 ? 'red' : 'dimmed'} fw={session.entriesConflicted > 0 ? 600 : undefined}>
                        {session.entriesConflicted > 0 ? session.entriesConflicted : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {session.chainVerified
                        ? (
                          <Group gap="xs">
                            <IconShield size={16} color="var(--mantine-color-green-6)" />
                            <Text size="sm" c="green">Verified</Text>
                          </Group>
                        )
                        : (
                          <Group gap="xs">
                            <IconX size={16} color="var(--mantine-color-red-6)" />
                            <Text size="sm" c="red">Failed</Text>
                          </Group>
                        )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(session.startedAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {session.entriesConflicted > 0 && (
                          <Button
                            size="xs"
                            variant="light"
                            color="red"
                            leftSection={<IconAlertTriangle size={12} />}
                            onClick={() => handleOpenConflicts(session)}
                          >
                            Conflicts
                          </Button>
                        )}
                        <Button
                          size="xs"
                          variant="subtle"
                          color={session.chainVerified ? 'green' : 'red'}
                          leftSection={<IconShield size={12} />}
                          onClick={() => handleOpenChainVerification(session)}
                        >
                          Verify Chain
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </>
      )}

      {/* Conflict Drawer */}
      <SyncConflictDrawer
        opened={conflictDrawerOpened}
        onClose={() => {
          conflictDrawerHandlers.close();
          setSelectedSessionId(null);
        }}
        sessionId={selectedSessionId}
      />

      {/* Chain Verification Drawer */}
      <ChainVerificationDrawer
        opened={chainDrawerOpened}
        onClose={() => {
          chainDrawerHandlers.close();
          setSelectedSessionId(null);
          setSelectedArchiveKey(null);
        }}
        sessionId={selectedSessionId}
        archiveKey={selectedArchiveKey}
      />
    </Stack>
  );
}

export default SyncDashboardPage;
